import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  DPE_ACTION_FIELDS,
  designationForCode,
  dpeActionLabel,
  productDescriptionForCode,
  statusPatch,
} from './dpeActionModel.js';
import { catalogOptionLabel, catalogOptions } from './varianceActionDialogModel.js';

const FIELD_LABELS = {
  referenceId: 'Reference ID',
  dealerPrice: 'Dealer price',
  filler: 'Filler',
  gmBrandCd: 'GM brand code',
  vendorCd: 'Vendor code',
  effectiveDate: 'Effective date',
  selectionDate: 'Selection date',
  billingDate: 'Billing date',
  lastUpdatedDate: 'Last updated date',
  productCd: 'Product code',
  productDescription: 'Product description',
  designation: 'Designation',
  status: 'Status',
};
const SYSTEM_FIELD_LABELS = {
  bac: 'BAC',
  countryCd: 'Country',
  categoryCd: 'Category',
  occurrence: 'Occurrence',
  itemQuantity: 'Quantity',
  lastUpdatedBy: 'Last updated by',
};
const SYSTEM_FIELDS = Object.keys(SYSTEM_FIELD_LABELS);
const STATUS_OPTIONS = ['Live', 'Pending Live', 'Pending Cancel', 'Cancelled'];
const DATE_FIELDS = new Set(['effectiveDate', 'selectionDate', 'billingDate', 'lastUpdatedDate']);
const DATE_PICKER_SX = {
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    filter: 'invert(47%) sepia(96%) saturate(2386%) hue-rotate(190deg) brightness(101%) contrast(101%)',
  },
};

const toInputDate = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})/);
  if (us) {
    const year = us[3].length === 2 ? `20${us[3]}` : us[3];
    return `${year}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

const fromInputDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value || '';
  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`;
};

const DpeActionDialog = ({ draft, onClose, onSave, catalogItems = [], busy = false, error = null }) => {
  const [actionType, setActionType] = useState('add');
  const [form, setForm] = useState({});
  const [datePreset, setDatePreset] = useState('');
  const datePresetTypedRef = useRef(false);
  const catalogRows = useMemo(() => catalogOptions(catalogItems), [catalogItems]);

  useEffect(() => {
    if (!draft) return;
    setActionType(draft.actionType || 'add');
    setForm(draft.vmtPayload || {});
    setDatePreset('');
    datePresetTypedRef.current = false;
  }, [draft]);

  const requiredMissing = useMemo(() => (
    ['referenceId', 'dealerPrice', 'gmBrandCd', 'vendorCd', 'effectiveDate', 'selectionDate', 'productCd', 'productDescription', 'designation', 'status']
      .filter((field) => !String(form[field] ?? '').trim())
  ), [form]);
  const canSave = Boolean(draft) && requiredMissing.length === 0;

  const applyProductCode = (productCode, option = null) => {
    setForm((current) => {
      const nextDescription = option?.displayName || productDescriptionForCode(productCode, current.productDescription);
      return {
        ...current,
        productCd: productCode,
        productDescription: nextDescription,
        designation: designationForCode(productCode),
        ...(option && option.standardPrice !== undefined && option.standardPrice !== null ? { dealerPrice: String(option.standardPrice) } : {}),
      };
    });
  };
  const setField = (field, value) => {
    if (field === 'productCd') {
      applyProductCode(value);
      return;
    }
    setForm((current) => ({ ...current, [field]: value }));
  };
  const applyStatus = (status) => setForm((current) => ({ ...current, ...statusPatch(status) }));
  const applyDatePresetValue = (value) => {
    if (!value) return;
    setForm((current) => ({
      ...current,
      effectiveDate: fromInputDate(value),
      selectionDate: fromInputDate(value),
      billingDate: fromInputDate(value),
    }));
  };
  const applyDatePreset = () => {
    applyDatePresetValue(datePreset);
    datePresetTypedRef.current = false;
  };
  const handleDatePresetChange = (event) => {
    const value = event.target.value;
    setDatePreset(value);
    if (!datePresetTypedRef.current && toInputDate(value) === value) applyDatePresetValue(value);
  };
  const handleSave = () => {
    if (!canSave) return;
    onSave({
      ...draft,
      actionType,
      vmtPayload: {
        ...(draft.vmtPayload || {}),
        ...form,
        status: actionType === 'remove' ? 'Cancelled' : form.status,
      },
    });
  };

  return (
    <Dialog open={Boolean(draft)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{dpeActionLabel(actionType)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Stage a full VMT product payload for DPE. This does not change billing totals; it only queues DPE work for review and send.
          </Alert>
          {error ? (
            <Alert severity="error">
              {error?.message || String(error)}
            </Alert>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              label="Action"
              value={actionType}
              onChange={(event) => {
                const next = event.target.value;
                setActionType(next);
                if (next === 'remove') applyStatus('Cancelled');
              }}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="add">Add product</MenuItem>
              <MenuItem value="remove">Remove product</MenuItem>
              <MenuItem value="change_price">Change price</MenuItem>
            </TextField>
            <TextField
              label="Set dates"
              type="date"
              value={datePreset}
              onKeyDown={() => {
                datePresetTypedRef.current = true;
              }}
              onPaste={() => {
                datePresetTypedRef.current = true;
              }}
              onClick={() => {
                datePresetTypedRef.current = false;
              }}
              onChange={handleDatePresetChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <Button size="small" startIcon={<EventAvailableOutlinedIcon />} onClick={applyDatePreset}>
                    Apply
                  </Button>
                ),
              }}
              sx={{ flex: 1, ...DATE_PICKER_SX }}
            />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {STATUS_OPTIONS.map((status) => (
              <Button
                key={status}
                size="small"
                variant={form.status === status ? 'contained' : 'outlined'}
                color={status === 'Cancelled' ? 'error' : status === 'Pending Cancel' ? 'warning' : 'primary'}
                onClick={() => applyStatus(status)}
              >
                Force {status}
              </Button>
            ))}
          </Stack>
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            {DPE_ACTION_FIELDS.map((field) => {
              if (field === 'productCd') {
                const selected = catalogRows.find((item) => item.productCode === form.productCd) || form.productCd || '';
                return (
                  <Autocomplete
                    key={field}
                    freeSolo
                    options={catalogRows}
                    filterOptions={(options, state) => {
                      const search = String(state.inputValue || '').trim().toLowerCase();
                      if (!search) return options;
                      return options.filter((option) => (
                        String(option.productCode || '').toLowerCase().includes(search)
                        || String(option.displayName || '').toLowerCase().includes(search)
                      ));
                    }}
                    value={selected}
                    inputValue={form.productCd ?? ''}
                    getOptionLabel={catalogOptionLabel}
                    onChange={(_event, option) => {
                      if (typeof option === 'string') applyProductCode(option);
                      else if (option?.productCode) applyProductCode(option.productCode, option);
                    }}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'input') {
                        const exactOption = catalogRows.find((item) => (
                          String(item.productCode || '').toUpperCase() === String(value || '').trim().toUpperCase()
                        ));
                        applyProductCode(value, exactOption || null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={FIELD_LABELS[field]}
                        required={requiredMissing.includes(field)}
                        helperText="Catalog-backed; typing still allows custom VMT product codes"
                      />
                    )}
                    fullWidth
                  />
                );
              }
              return (
                <TextField
                  key={field}
                  label={FIELD_LABELS[field] || field}
                  type={DATE_FIELDS.has(field) ? 'date' : undefined}
                  value={DATE_FIELDS.has(field) ? toInputDate(form[field]) : (form[field] ?? '')}
                  onChange={(event) => setField(field, DATE_FIELDS.has(field) ? fromInputDate(event.target.value) : event.target.value)}
                  select={field === 'status'}
                  required={requiredMissing.includes(field)}
                  InputLabelProps={DATE_FIELDS.has(field) ? { shrink: true } : undefined}
                  sx={DATE_FIELDS.has(field) ? DATE_PICKER_SX : undefined}
                  fullWidth
                >
                  {field === 'status' ? STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  )) : null}
                </TextField>
              );
            })}
          </Stack>
          {requiredMissing.length > 0 && (
            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
              Missing required fields: {requiredMissing.map((field) => FIELD_LABELS[field] || field).join(', ')}
            </Typography>
          )}
          <Stack spacing={1}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              System fields
            </Typography>
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              {SYSTEM_FIELDS.map((field) => (
                <TextField
                  key={field}
                  label={SYSTEM_FIELD_LABELS[field]}
                  value={form[field] ?? ''}
                  InputProps={{ readOnly: true }}
                  size="small"
                  fullWidth
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || busy} onClick={handleSave}>Queue DPE action</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DpeActionDialog;
